import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Clock, Plus, Trash2, Save, Loader2, CalendarClock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClinicDataPanel } from "@/components/settings/ClinicDataPanel";
import { BotMessagesPanel } from "@/components/settings/BotMessagesPanel";
import { TeamPanel } from "@/components/settings/TeamPanel";
import { ChangePasswordPanel } from "@/components/settings/ChangePasswordPanel";
import { getScheduleSlots, saveScheduleSlots, getRgpdSettings, saveRgpdSettings } from "@/lib/dentiumApi";

const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

export default function ConfiguracionPage({ user }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState([]);
  const { data: slots } = useQuery({ queryKey: ["schedule-slots"], queryFn: getScheduleSlots });

  useEffect(() => {
    if (slots) setRows(slots.map((s) => ({ weekday: s.weekday, start: s.start, end: s.end })));
  }, [slots]);

  const [rgpd, setRgpd] = useState(null);
  const { data: rgpdData } = useQuery({ queryKey: ["rgpd-settings"], queryFn: getRgpdSettings });
  useEffect(() => { if (rgpdData) setRgpd(rgpdData); }, [rgpdData]);

  const saveRgpd = useMutation({
    mutationFn: () => saveRgpdSettings(rgpd),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rgpd-settings"] });
      toast.success("Protección de datos guardada", { description: rgpd?.enabled ? "El bot pedirá consentimiento antes de atender." : "Aviso RGPD desactivado." });
    },
    onError: () => toast.error("No se pudo guardar"),
  });
  const setR = (field, value) => setRgpd((r) => ({ ...r, [field]: value }));

  const save = useMutation({
    mutationFn: () => saveScheduleSlots(rows.map((r) => ({ weekday: Number(r.weekday), start: r.start, end: r.end }))),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule-slots"] });
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["bot-messages"] });
      toast.success("Horarios guardados", { description: "El bot usará estos horarios a partir de ahora." });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudieron guardar los horarios"),
  });

  const addRow = () => setRows((r) => [...r, { weekday: 1, start: "09:00", end: "10:00" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));

  const valid = rows.every((r) => r.start && r.end && r.start < r.end);

  return (
    <div className="h-full overflow-y-auto" data-testid="configuracion-page">
    <div className="p-4 sm:p-6 lg:p-8 max-w-[800px] mx-auto">
      <ClinicDataPanel />
      <div className="rounded-2xl mt-5 border border-[#E7E2DA] bg-white p-5 sm:p-6" data-testid="schedule-config-panel">
        <div className="flex items-center gap-2 mb-1">
          <span className="h-9 w-9 rounded-xl bg-[#F1EBE1] flex items-center justify-center">
            <CalendarClock className="h-4.5 w-4.5 text-[#1A1A1A]" />
          </span>
          <h2 className="text-lg font-medium text-[#1A1A1A]">Horarios de atención para citas</h2>
        </div>
        <p className="text-sm text-[#8A7E70] mb-5 ml-11">
          El bot usa SIEMPRE estos horarios para saber cuándo hay atención de citas. Añade, edita o elimina franjas.
        </p>

        <div className="space-y-3">
          {rows.length === 0 && (
            <div className="text-center text-sm text-[#A79E90] py-6 border border-dashed border-[#E0D3BC] rounded-xl">
              No hay franjas. Pulsa “Añadir franja”.
            </div>
          )}
          {rows.map((row, i) => (
            <div key={i} data-testid="schedule-slot-row" className="flex flex-wrap items-center gap-2 rounded-xl border border-[#EDE7DE] bg-[#FAF8F5] p-3">
              <Select value={String(row.weekday)} onValueChange={(v) => updateRow(i, "weekday", Number(v))}>
                <SelectTrigger data-testid="slot-day-select" className="h-10 w-[150px] bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS.map((d, idx) => (
                    <SelectItem key={idx} value={String(idx)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-[#A79E90]" />
                <Input
                  type="time"
                  data-testid="slot-start-input"
                  value={row.start}
                  onChange={(e) => updateRow(i, "start", e.target.value)}
                  className="h-10 w-[120px] bg-white"
                />
                <span className="text-[#A79E90]">–</span>
                <Input
                  type="time"
                  data-testid="slot-end-input"
                  value={row.end}
                  onChange={(e) => updateRow(i, "end", e.target.value)}
                  className="h-10 w-[120px] bg-white"
                />
              </div>
              <button
                data-testid="slot-delete-btn"
                onClick={() => removeRow(i)}
                title="Eliminar franja"
                className="ml-auto h-9 w-9 rounded-full flex items-center justify-center text-[#A79E90] hover:bg-red-100 hover:text-red-600 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mt-5 flex-wrap">
          <Button variant="outline" data-testid="add-slot-btn" onClick={addRow} className="h-11 border-[#E0D3BC]">
            <Plus className="h-4 w-4 mr-1.5" /> Añadir franja
          </Button>
          <Button
            data-testid="save-slots-btn"
            onClick={() => valid && save.mutate()}
            disabled={save.isPending || !valid}
            className="h-11 px-6 bg-[#1A1A1A] hover:bg-black text-white disabled:opacity-60"
          >
            {save.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando…</> : <><Save className="h-4 w-4 mr-2" /> Guardar cambios</>}
          </Button>
        </div>
        {!valid && rows.length > 0 && (
          <p className="text-xs text-red-500 mt-2">La hora de fin debe ser posterior a la de inicio en todas las franjas.</p>
        )}
      </div>

      <BotMessagesPanel />

      {/* Protección de datos (RGPD) */}
      {rgpd && (
        <div className="rounded-2xl border border-[#E7E2DA] bg-white p-5 sm:p-6 mt-5" data-testid="rgpd-config-panel">
          <div className="flex items-center gap-2 mb-1">
            <span className="h-9 w-9 rounded-xl bg-[#F1EBE1] flex items-center justify-center">
              <ShieldCheck className="h-4.5 w-4.5 text-[#1A1A1A]" />
            </span>
            <h2 className="text-lg font-medium text-[#1A1A1A]">Protección de datos</h2>
          </div>
          <p className="text-sm text-[#8A7E70] mb-5 ml-11">
            Si activas el aviso RGPD, el bot mostrará el texto legal en el primer mensaje y pedirá la aceptación del paciente antes de continuar. Sin aceptación, no responde.
          </p>

          <div className="flex items-center justify-between gap-3 rounded-xl border border-[#EDE7DE] bg-[#FAF8F5] px-4 py-3 mb-4">
            <div>
              <div className="text-sm font-medium text-[#1A1A1A]">Aviso RGPD activado</div>
              <div className="text-xs text-[#8A7E70]">Pide consentimiento antes de atender</div>
            </div>
            <Switch data-testid="rgpd-enabled-switch" checked={rgpd.enabled} onCheckedChange={(v) => setR("enabled", v)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">Responsable del tratamiento</Label>
              <Input data-testid="rgpd-responsable" value={rgpd.responsable} onChange={(e) => setR("responsable", e.target.value)} placeholder="Nombre de la clínica" />
            </div>
            <div>
              <Label className="mb-1.5 block">Empresa</Label>
              <Input data-testid="rgpd-empresa" value={rgpd.empresa} onChange={(e) => setR("empresa", e.target.value)} placeholder="Razón social" />
            </div>
            <div>
              <Label className="mb-1.5 block">NIF / CIF</Label>
              <Input data-testid="rgpd-nif" value={rgpd.nif} onChange={(e) => setR("nif", e.target.value)} placeholder="B12345678" />
            </div>
            <div>
              <Label className="mb-1.5 block">Email de contacto</Label>
              <Input data-testid="rgpd-email" type="email" value={rgpd.email} onChange={(e) => setR("email", e.target.value)} placeholder="privacidad@tuclinica.com" />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1.5 block">Dirección postal</Label>
              <Input data-testid="rgpd-direccion" value={rgpd.direccion} onChange={(e) => setR("direccion", e.target.value)} placeholder="Calle, número, ciudad, CP" />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1.5 block">Texto legal</Label>
              <Textarea
                data-testid="rgpd-texto-legal"
                value={rgpd.texto_legal}
                onChange={(e) => setR("texto_legal", e.target.value)}
                rows={4}
                maxLength={2000}
                className="resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end mt-5">
            <Button
              data-testid="save-rgpd-btn"
              onClick={() => saveRgpd.mutate()}
              disabled={saveRgpd.isPending}
              className="h-11 px-6 bg-[#1A1A1A] hover:bg-black text-white disabled:opacity-60"
            >
              {saveRgpd.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando…</> : <><Save className="h-4 w-4 mr-2" /> Guardar cambios</>}
            </Button>
          </div>
        </div>
      )}

      <TeamPanel currentUser={user} />
      <ChangePasswordPanel />
    </div>
    </div>
  );
}
